import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { Button, Input, Modal } from '../../../../components';
import {
  ButtonType,
  ButtonVariantType,
} from '../../../../components/Button/Button';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import { crmInputAppsIcon, errorIcon, successIcon } from './assets';
import {
  resetCrmRequestStatus,
  SendContactCrmRequest,
} from '../../../../store/auth/auth.actions';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const ConnectCrm: React.FC<Props> = ({
  open,
  onClose,
}: Props): JSX.Element => {
  const { user, crmRequestStatus } = useSelector(
    (state: RootState) => state.auth
  );

  const [dropdownValue, setDropdownValue] = useState('');

  const [inputValue, setInputValue] = useState('');

  const [requestSent, setRequestSent] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (dropdownValue === 'Other') {
      setInputValue('');
    }
  }, [dropdownValue]);

  const table = [
    { label: 'SalesForce', value: 'sales-force' },
    { label: 'HubSpot', value: 'hub-spot' },
    { label: 'Zoho', value: 'zoho' },
    { label: 'Pipedrive', value: 'sales-force' },
    { label: 'Microsoft Dynamics', value: 'microsoft-dynamics' },
    { label: 'Other', value: '' },
  ];

  const changeValue = (newValue: typeof table[0]) =>
    setDropdownValue(newValue.label);

  const changeInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
  };

  const closeAndCrear = (needDipatch?: boolean) => {
    onClose();
    setInputValue('');
    setDropdownValue('');
    setRequestSent(false);
    if (needDipatch) {
      dispatch(resetCrmRequestStatus());
    }
  };

  const clickGotIt = () => {
    if (typeof crmRequestStatus === 'string') {
      setInputValue('');
      setDropdownValue('');
      closeAndCrear();
      setTimeout(() => {
        dispatch(resetCrmRequestStatus());
      }, 300);
    }
  };

  const sendCrmRequest = () => {
    if (user && (dropdownValue.length || inputValue.length)) {
      const crmValueToSend = inputValue.length ? inputValue : dropdownValue;

      dispatch(SendContactCrmRequest(user.userId, user.orgId, crmValueToSend));
      setRequestSent(true);
    }
  };

  const createFieldErrorMessage = () => {
    let error = '';

    if (inputValue.length > 50) {
      error = 'You’ve reached the maximum character limit';
    }

    return error;
  };

  const buttonDisabled =
    createFieldErrorMessage().length > 0 ||
    (!dropdownValue.length && !inputValue.length) ||
    (dropdownValue === 'Other' && !inputValue.length) ||
    requestSent;

  const attrs = {
    actionBodyWrapper: {
      className: 'crm-body__action',
    },

    statusBody: {
      wrapper: {
        className: 'crm-body__status',
      },

      icon: {
        className: 'crm-body__status-img',
        src: crmRequestStatus === 'success' ? successIcon : errorIcon,
      },

      boldMessage: {
        className: 'crm-body__status-bold-text',
      },

      message: {
        className: 'crm-body__status-text',
      },
    },

    Modal: {
      title: crmRequestStatus?.length
        ? ''
        : 'What CRM would you like to connect?',
      open,
      onClose: crmRequestStatus?.length
        ? clickGotIt
        : () => closeAndCrear(true),
      width: crmRequestStatus?.length ? '400px' : '600px',
      className: 'crm-modal',
      empty: typeof crmRequestStatus === 'string',
    },

    Select: {
      label: 'CRM',
      placeholder: 'Select your CRM',
      icon: crmInputAppsIcon,
      isRequired: true,
      medium: true,
      isInCrmWindow: true,
      items: table,
      onChange: changeValue,
      style: {
        padding: '25px 17px',
        background: '#F8F8FB',
        justifyContent: 'space-between',
        borderRadius: '8px',
      },
      margin: '40px 0px 0px',
      padding: '0px 12px 0px 0px',
    },

    Input: {
      label: '',
      value: inputValue,
      onChange: changeInputValue,
      name: 'lastName',
      className: 'edit-user__input',
      error: createFieldErrorMessage(),
      errorOnTheRight: true,
    },

    buttons: {
      Send: {
        type: 'submit' as ButtonType,
        className: 'crm-body-button',
        variant: 'common' as ButtonVariantType,
        disabled: buttonDisabled,
        onClick: sendCrmRequest,
      },

      GotIt: {
        type: 'submit' as ButtonType,
        className: 'crm-body-button',
        variant: 'common' as ButtonVariantType,
        onClick: clickGotIt,
      },
    },
  };

  const input = dropdownValue === 'Other' ? <Input {...attrs.Input} /> : null;

  const actionBody = !crmRequestStatus ? (
    <div className="crm-body__action">
      <CustomSelect {...attrs.Select} />
      {input}
      <Button {...attrs.buttons.Send}>Send</Button>
    </div>
  ) : null;

  const statusBody =
    typeof crmRequestStatus === 'string' ? (
      <div {...attrs.statusBody.wrapper}>
        <img {...attrs.statusBody.icon} alt="" />
        <div {...attrs.statusBody.boldMessage}>
          {crmRequestStatus === 'success'
            ? 'Your request has been sent'
            : 'Something went wrong'}
        </div>
        <div {...attrs.statusBody.message}>
          {crmRequestStatus === 'success'
            ? 'We will get back to you shortly'
            : 'Please refresh and try again'}
        </div>
        <Button {...attrs.buttons.GotIt}>Got it</Button>
      </div>
    ) : null;

  return (
    <Modal {...attrs.Modal}>
      <>
        {actionBody}
        {statusBody}
      </>
    </Modal>
  );
};
